import { AnyAction, createAsyncThunk, ThunkDispatch } from "@reduxjs/toolkit";
import { bffApi, EmailInviteContextDto } from "../../../../autogen/bff-api";
import { authService } from "../../../../config";
import {
  clearOrganizationId,
  getOrganizationId,
  setOrganizationId,
} from "../../../local-storage/selected-organization";
import { AuthState } from "../../reducers/authStateReducer";
import { LoginError } from "./login-thunk";

export const getInitialAuthStateThunk = createAsyncThunk(
  "authState/getInitialAuthState",
  async (_: void, { dispatch }): Promise<AuthState> => {
    try {
      await authService.getSession();

      return await getLoggedInState({
        showUnknownError: false,
        dispatch: dispatch,
        invitationContext: null,
      });
    } catch (err) {
      return {
        type: "LoggedOut",
        isLoggingIn: false,
        loginError: null,
        isSigningUp: false,
        signupError: null,
      };
    }
  }
);

export const getLoggedInState = async (props: {
  showUnknownError: boolean;
  invitationContext: EmailInviteContextDto | null;
  dispatch: ThunkDispatch<unknown, unknown, AnyAction>;
  preferredOrganizationId?: string | null;
}): Promise<AuthState> => {
  let { meResult, orgResult } = await fetchMeAndOrganizations(props.dispatch);
  if (getOrganizationId() !== null && (isForbidden(meResult.error) || isForbidden(orgResult.error))) {
    clearOrganizationId();
    ({ meResult, orgResult } = await fetchMeAndOrganizations(props.dispatch));
  }

  if (meResult.data && orgResult.data) {
    const orgs = orgResult.data.orgs;
    if (orgs.length > 0) {
      const preferredOrgId = props.preferredOrganizationId ?? getOrganizationId();
      const selectedOrg = orgs.find((e) => e.id === preferredOrgId) ?? orgs[0];
      setOrganizationId(selectedOrg.id);
      return {
        type: "LoggedInWithOrgContext",
        me: meResult.data,
        organizations: orgs,
        selectedOrg,
      };
    } else {
      return {
        type: "LoggedIn",
        me: meResult.data,
        organizations: [],
        invitationContext: props.invitationContext,
        isCreatingOrg: false,
        createOrgError: null,
      };
    }
  } else {
    await authService.logout();
    const unknownError = props.showUnknownError ? "UnknownError" : null;
    const loginError =
      (meResult.error as { data: { error: LoginError } })?.data?.error ??
      (meResult.error as { data: { errorCode: string } })?.data?.errorCode;
    return {
      type: "LoggedOut",
      isLoggingIn: false,
      loginError: loginError ?? unknownError,
      isSigningUp: false,
      signupError: null,
    };
  }
};

const fetchMeAndOrganizations = async (dispatch: ThunkDispatch<unknown, unknown, AnyAction>) => {
  const meResponse = dispatch(bffApi.endpoints.getMe.initiate({}, { forceRefetch: true }));
  meResponse.unsubscribe();
  const meResult = await meResponse;

  const orgResponse = dispatch(bffApi.endpoints.listOrganizationsForMe.initiate({}, { forceRefetch: true }));
  orgResponse.unsubscribe();
  const orgResult = await orgResponse;

  return { meResult, orgResult };
};

const isForbidden = (error: unknown): boolean =>
  typeof error === "object" && error !== null && "status" in error && error.status === 403;
